import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", recipient: "general", message: "" };

export const Contact = ({ t }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.successTitle, { description: t.contact.success });
      setForm(EMPTY);
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-xl bg-[#F8F7F4] border border-slate-200 text-[#0A272C] placeholder-slate-400 focus:outline-none focus:border-[#003940] focus:ring-1 focus:ring-[#003940] transition-all text-sm";

  return (
    <section id="kontakt" data-testid="contact-section" className="relative py-24 sm:py-32 px-4">
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-3xl mx-auto text-center">
          <Eyebrow>{t.contact.label}</Eyebrow>
          <h2 className="mt-4 font-jakarta text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
            {t.contact.title}
          </h2>
          <p className="mt-5 text-base text-slate-600 leading-relaxed">{t.contact.sub}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="mt-12 max-w-2xl mx-auto p-8 sm:p-12 rounded-3xl bg-surface border border-slate-200 shadow-2xl space-y-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="contact-name" className="block text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">
                  {t.contact.name}
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  type="text"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder={t.contact.namePh}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">
                  {t.contact.email}
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder={t.contact.emailPh}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label htmlFor="contact-message" className="block text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-2">
                {t.contact.message}
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder={t.contact.messagePh}
                className={`${inputClass} resize-none`}
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#003940] hover:bg-[#002A2F] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm transition-all duration-200"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              <span key={sending ? "sending" : "idle"}>{sending ? t.contact.sending : t.contact.submit}</span>
            </button>

            <p className="flex items-center justify-center gap-2 text-xs text-slate-500 font-mono">
              <ShieldCheck size={14} className="text-[#003940]" />
              {t.contact.free}
            </p>
            <p data-testid="contact-ai-hint" className="flex items-center justify-center gap-2 text-xs text-[#003940] font-mono">
              <Sparkles size={14} />
              {t.contact.aiHint}
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
